/**
 * Views Tab - CRUD interface for saved SOQL views.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../api/analytics';
import SOQLEditor from '../query/SOQLEditor';

export default function ViewsTab({ metadata }) {
  const navigate = useNavigate();
  const [views, setViews] = useState([]);
  const [selectedView, setSelectedView] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditor, setShowEditor] = useState(false);

  // Editor state
  const [editorName, setEditorName] = useState('');
  const [editorDescription, setEditorDescription] = useState('');
  const [editorCategory, setEditorCategory] = useState('');
  const [editorSOQL, setEditorSOQL] = useState('');
  const [editorErrors, setEditorErrors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  // Results state
  const [results, setResults] = useState(null);

  // Load views
  const loadViews = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.fetchViews();
      setViews(response?.views || []);
    } catch (err) {
      setError(err.message || 'Failed to load views');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  // Open editor for new view
  const handleNewView = () => {
    setSelectedView(null);
    setEditorName('');
    setEditorDescription('');
    setEditorCategory('');
    setEditorSOQL('SELECT Id, Name FROM Account LIMIT 10');
    setEditorErrors([]);
    setResults(null);
    setShowEditor(true);
  };

  // Create a free-mode sheet (no SOQL, blank spreadsheet)
  const [isCreatingFreeSheet, setIsCreatingFreeSheet] = useState(false);
  const handleNewFreeSheet = async () => {
    const name = prompt('Name for your free sheet:');
    if (!name?.trim()) return;
    setIsCreatingFreeSheet(true);
    try {
      const result = await api.createView({
        name: name.trim(),
        sheet_mode: 'free',
      });
      await loadViews();
      navigate(`/analytics/views/${result.id}/spreadsheet`);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create free sheet');
    } finally {
      setIsCreatingFreeSheet(false);
    }
  };

  // Open editor for existing view
  const handleEditView = async (viewId) => {
    try {
      const response = await api.fetchView(viewId);
      const view = response;
      setSelectedView(view);
      setEditorName(view.name);
      setEditorDescription(view.description || '');
      setEditorCategory(view.category || '');
      setEditorSOQL(view.soql_content);
      setEditorErrors([]);
      setResults(null);
      setShowEditor(true);
    } catch (err) {
      setError(err.message || 'Failed to load view');
    }
  };

  // Validate SOQL
  const handleValidate = async () => {
    if (!editorSOQL.trim()) {
      setEditorErrors([{ line: 1, column: 1, message: 'Query cannot be empty' }]);
      return false;
    }

    try {
      const response = await api.validateView(editorSOQL);
      if (response?.valid) {
        setEditorErrors([]);
        return true;
      } else {
        setEditorErrors(response?.errors || []);
        return false;
      }
    } catch (err) {
      setEditorErrors([{ line: 1, column: 1, message: err.message }]);
      return false;
    }
  };

  // Save view
  const handleSave = async () => {
    if (!editorName.trim()) {
      setEditorErrors([{ message: 'Name is required' }]);
      return;
    }

    const isValid = await handleValidate();
    if (!isValid) return;

    setIsSaving(true);
    try {
      if (selectedView) {
        // Update existing view
        await api.updateView(selectedView.id, {
          name: editorName,
          description: editorDescription,
          category: editorCategory || null,
          soql_content: editorSOQL,
        });
      } else {
        // Create new view
        await api.createView({
          name: editorName,
          description: editorDescription,
          category: editorCategory || null,
          soql_content: editorSOQL,
        });
      }

      await loadViews();
      setShowEditor(false);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save view';
      setEditorErrors([{ message: errorMsg }]);
    } finally {
      setIsSaving(false);
    }
  };

  // Execute view
  const handleExecute = async () => {
    if (selectedView) {
      setIsExecuting(true);
      try {
        const response = await api.executeView(selectedView.id, {}, 1000);
        setResults({
          columns: response?.columns || [],
          rows: response?.rows || [],
          rowCount: response?.row_count || 0,
          truncated: response?.truncated || false,
          durationMs: response?.duration_ms || 0,
        });
      } catch (err) {
        setResults({ error: err.message || 'Query execution failed' });
      } finally {
        setIsExecuting(false);
      }
    }
  };

  // Delete view
  const handleDelete = async (viewId) => {
    if (!window.confirm('Are you sure you want to delete this view?')) return;

    try {
      await api.deleteView(viewId);
      await loadViews();
      if (selectedView?.id === viewId) {
        setShowEditor(false);
        setSelectedView(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete view');
    }
  };

  // Export view
  const handleExport = async (viewId) => {
    try {
      const exportData = await api.exportViews([viewId]);
      // Download as JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const view = views.find(v => v.id === viewId);
      a.download = `${view?.slug || 'view'}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to export view');
    }
  };

  // Import view from file
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const exportData = JSON.parse(text);
      const result = await api.importViews(exportData);

      if (result.errors?.length > 0) {
        setError(`Import completed with warnings: ${result.errors.join(', ')}`);
      }

      await loadViews();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to import view');
    }

    // Reset file input
    event.target.value = '';
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Saved Views</h2>
        <div className="flex gap-2">
          <label className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition">
            Import
            <input
              type="file"
              accept=".json"
              onChange={handleImportFile}
              className="hidden"
            />
          </label>
          <button
            onClick={handleNewFreeSheet}
            disabled={isCreatingFreeSheet}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
            data-testid="new-free-sheet-btn"
          >
            {isCreatingFreeSheet ? 'Creating…' : '+ Free Sheet'}
          </button>
          <button
            onClick={handleNewView}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
          >
            + New View
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Views List or Editor */}
      {showEditor ? (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex justify-between items-center border-b pb-4">
            <h3 className="text-lg font-medium">
              {selectedView ? 'Edit View' : 'New View'}
            </h3>
            <button
              onClick={() => setShowEditor(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
          </div>

          {/* Name & Description */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={editorName}
                onChange={(e) => setEditorName(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="My View"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <input
                type="text"
                value={editorCategory}
                onChange={(e) => setEditorCategory(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="Sales, Pipeline, etc."
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              type="text"
              value={editorDescription}
              onChange={(e) => setEditorDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="What does this view do?"
            />
          </div>

          {/* SOQL Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              SOQL Query
            </label>
            <SOQLEditor
              value={editorSOQL}
              onChange={setEditorSOQL}
              onExecute={selectedView ? handleExecute : undefined}
              metadata={metadata}
              height="200px"
            />
          </div>

          {/* Validation Errors */}
          {editorErrors.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {editorErrors.map((err, i) => (
                <div key={i}>
                  {err.line ? `Line ${err.line}: ` : ''}{err.message}
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            {selectedView && (
              <button
                onClick={handleExecute}
                disabled={isExecuting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {isExecuting ? 'Running...' : 'Execute'}
              </button>
            )}
            <button
              onClick={() => setShowEditor(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>

          {/* Results */}
          {results && (
            <div className="mt-4 border-t pt-4">
              {results.error ? (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg">
                  {results.error}
                </div>
              ) : (
                <div>
                  <div className="text-sm text-gray-500 mb-2">
                    {results.rowCount} rows {results.truncated && '(truncated)'} in {results.durationMs}ms
                  </div>
                  <div className="max-h-64 overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {results.columns?.map((col) => (
                            <th key={col} className="px-3 py-2 text-left font-medium text-gray-700">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {results.rows?.slice(0, 100).map((row, i) => (
                          <tr key={i} className={i % 2 ? 'bg-gray-50' : ''}>
                            {results.columns?.map((col) => (
                              <td key={col} className="px-3 py-2 text-gray-900">
                                {row[col]?.toString() || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Views List */
        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">Loading views...</div>
          ) : views.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p className="mb-4">No views yet. Create your first saved view to get started.</p>
              <button
                onClick={handleNewView}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Create View
              </button>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Source</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Created</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {views.map((view) => (
                  <tr key={view.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {view.name}
                        {view.sheet_mode === 'free' && (
                          <span className="text-xs font-normal text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">Free</span>
                        )}
                      </div>
                      {view.description && (
                        <div className="text-sm text-gray-500">{view.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {view.category || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        view.source === 'reference'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {view.source}
                        {view.modified && ' (modified)'}
                        {view.pinned && ' (pinned)'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(view.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        {view.sheet_mode !== 'free' && (
                          <button
                            onClick={() => handleEditView(view.id)}
                            className="text-primary-600 hover:text-primary-800"
                          >
                            Edit
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/analytics/views/${view.id}/spreadsheet`)}
                          className="text-green-600 hover:text-green-800"
                          data-testid="open-spreadsheet-btn"
                        >
                          Spreadsheet
                        </button>
                        <button
                          onClick={() => handleExport(view.id)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Export
                        </button>
                        <button
                          onClick={() => handleDelete(view.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
